import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    RichTextBold,
    RichTextBulletList,
    RichTextItalic,
    RichTextLink,
    RichTextOrderedList,
    RichTextStrikethrough,
    RichTextToolbar,
    RichTextToolbarDivider,
    RichTextToolbarOverflow,
    RichTextToolbarSpacer,
    RichTextUnderline,
} from '@plato/ui/components/rich-text-toolbar';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useCallback, type ReactNode } from 'react';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import type { VoiceInputPayload } from '../../analytics/types/analytics-events.ts';
import { VoiceRecordingButton } from '../../voice-recording/components/voice-recording-button.tsx';

type Props = {
    /** Analytics attribution for the voice mic — which field/composer this toolbar belongs to. */
    trackingContext: VoiceInputPayload;
    disableVoiceRecording?: boolean;
};

/**
 * The bar is one plugin per control: each renders its button and registers its ⋮
 * row, so `RichTextToolbarOverflow` is exactly what the bar hid.
 */
export function ToolbarPlugin({ trackingContext, disableVoiceRecording = false }: Props): ReactNode {
    const { enableVoiceRecording } = useAppConfig();
    const { t } = useLocale();
    const [editor] = useLexicalComposerContext();

    const insertTranscribedText = useCallback(
        (text: string) => {
            editor.update(() => {
                const root = $getRoot();

                if (!root.getTextContent()) {
                    root.clear();
                }

                const paragraphNode = $createParagraphNode();
                const textNode = $createTextNode(text);
                paragraphNode.append(textNode);
                root.append(paragraphNode);
            });
        },
        [editor],
    );

    const showVoiceRecordingButton = enableVoiceRecording && !disableVoiceRecording;

    return (
        <RichTextToolbar>
            <RichTextBold label={t('rich_text_editor__format_bold')} />
            <RichTextItalic label={t('rich_text_editor__format_italic')} />
            <RichTextUnderline label={t('rich_text_editor__format_underline')} collapse />
            <RichTextStrikethrough label={t('rich_text_editor__format_strikethrough')} collapse />
            <RichTextToolbarDivider collapse />
            <RichTextOrderedList label={t('rich_text_editor__insert_numbered_list')} collapse />
            <RichTextBulletList label={t('rich_text_editor__insert_bullet_list')} collapse />
            <RichTextToolbarDivider collapse />
            <RichTextLink
                label={t('rich_text_editor__insert_link')}
                urlPrompt={t('rich_text_editor__enter_url')}
                collapse
            />
            <RichTextToolbarOverflow label={t('rich_text_editor__more_options')} />

            {showVoiceRecordingButton && (
                <>
                    <RichTextToolbarSpacer />
                    <VoiceRecordingButton
                        trackingContext={trackingContext}
                        onTranscription={insertTranscribedText}
                    />
                </>
            )}
        </RichTextToolbar>
    );
}
